import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { useCurrency } from '@/contexts/CurrencyContext'
import { toastHelpers } from '@/lib/toast-helpers'
import { dayLabel, downloadCSV, METHOD_LABEL, monthLabel, RECOVERY_LABEL, todayISO } from '@/lib/hr'
import { cn } from '@/lib/utils'
import type { HREmployee, HRLeaveBalances, HRLeaveEntry, HRRate, HRSettings, HRStatement, HRStatementLine } from '@/types/hr'
import { Banner, Empty, Field, Segmented, Stat, errMsg } from './shared'
import { EMPTY_PERSON, PersonForm, personPayload, type PersonFormValue } from './PersonForm'
import { AdvanceDialog, PayDialog, invalidateHRMoney } from './MoneyDialogs'
import { payLabel } from './PeopleTab'
import { hrRole } from './HRPage'

// One person (F-HR-N1 / N4): profile, pay history, their statement — every
// rupee owed, earned, advanced and paid with a running balance — and leave.

type SheetTab = 'profile' | 'pay' | 'statement' | 'leave'

function toForm(e: HREmployee): PersonFormValue {
  return {
    ...EMPTY_PERSON,
    full_name: e.full_name,
    phone: e.phone ?? '',
    designation: e.designation ?? '',
    department: e.department,
    join_date: e.join_date,
    weekly_off_days: e.weekly_off_days,
    shift_start: e.shift_start ?? '',
    emergency_contact: e.emergency_contact ?? '',
    notes: e.notes ?? '',
    cnic: e.cnic ?? '',
    pay_type: e.pay_type,
    absence_divisor: e.absence_divisor ?? '',
    payout_method: e.payout_method ?? 'cash',
    bank_name: e.bank_name ?? '',
    account_title: e.account_title ?? '',
    account_number: e.account_number ?? '',
  }
}

const ENTRY_LABEL: Record<HRStatementLine['entry_type'], string> = {
  opening_balance: 'Owed when Staff & Payroll started',
  salary_earned: 'Salary',
  bonus: 'Bonus',
  deduction: 'Deduction',
  leave_encashment: 'Leave paid out',
  advance_given: 'Advance given',
  advance_written_off: 'Advance written off',
  payout: 'Paid',
  reversal: 'Reversed',
}

export function PersonSheet({ id, onClose, settings }: { id?: string; onClose: () => void; settings?: HRSettings }) {
  const { seesPay } = hrRole()
  const [tab, setTab] = useState<SheetTab>('profile')
  useEffect(() => setTab('profile'), [id])
  const q = useQuery({ queryKey: ['hr', 'employee', id], queryFn: () => apiClient.hrGetEmployee(id!), enabled: !!id })
  const e = q.data?.data?.employee as HREmployee | undefined
  const rates = (q.data?.data?.rates as HRRate[] | undefined) ?? []
  const tabs: { id: SheetTab; label: string }[] = [
    { id: 'profile', label: 'Profile' },
    ...(seesPay ? [{ id: 'pay' as const, label: 'Pay' }, { id: 'statement' as const, label: 'Statement' }] : []),
    { id: 'leave', label: 'Leave' },
  ]
  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent size="lg">
        <SheetHeader>
          <SheetTitle>{e?.full_name ?? '…'}</SheetTitle>
          <SheetDescription>
            {e ? [e.designation, e.department, e.status === 'left' ? `Left ${e.exit_date}` : `Joined ${e.join_date}`].filter(Boolean).join(' · ') : ''}
          </SheetDescription>
        </SheetHeader>
        <SheetBody className="space-y-4">
          <Segmented value={tab} onChange={setTab} options={tabs} />
          {!e ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : tab === 'profile' ? (
            <ProfilePane e={e} settings={settings} />
          ) : tab === 'pay' ? (
            <PayPane e={e} rates={rates} />
          ) : tab === 'statement' ? (
            <StatementPane e={e} />
          ) : (
            <LeavePane e={e} />
          )}
        </SheetBody>
      </SheetContent>
    </Sheet>
  )
}

function ProfilePane({ e, settings }: { e: HREmployee; settings?: HRSettings }) {
  const qc = useQueryClient()
  const { isAdmin, canMark } = hrRole()
  const base = useMemo(() => toForm(e), [e])
  const [value, setValue] = useState(base)
  const [exitDate, setExitDate] = useState(todayISO())
  useEffect(() => setValue(base), [base])
  const dirty = JSON.stringify(value) !== JSON.stringify(base)
  const save = useMutation({
    mutationFn: () => apiClient.hrUpdateEmployee(e.id, personPayload(value, { admin: isAdmin, base })),
    onSuccess: () => {
      toastHelpers.success('Saved')
      qc.invalidateQueries({ queryKey: ['hr'] })
    },
    onError: (err) => toastHelpers.error('Couldn’t save', errMsg(err)),
  })
  const left = useMutation({
    mutationFn: () => apiClient.hrMarkLeft(e.id, exitDate),
    onSuccess: (res) => {
      const d = res.data?.encashed_days ?? 0
      toastHelpers.success('Marked as left', d ? `${d} unused leave days will be paid out on the leaving month’s payroll` : undefined)
      qc.invalidateQueries({ queryKey: ['hr'] })
    },
    onError: (err) => toastHelpers.error('Couldn’t mark as left', errMsg(err)),
  })
  const rejoin = useMutation({
    mutationFn: () => apiClient.hrRejoin(e.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr'] }),
    onError: (err) => toastHelpers.error('Couldn’t update', errMsg(err)),
  })
  return (
    <div className="space-y-4">
      {e.username && (
        <p className="text-xs text-muted-foreground">
          POS login: {e.username}
          {e.login_active === false ? ' (switched off)' : ''}
          {e.has_allowance ? ' · has a meal allowance' : ''}
        </p>
      )}
      {canMark ? (
        <>
          <PersonForm value={value} onChange={setValue} departments={settings?.departments ?? [e.department]} admin={isAdmin} />
          <div className="flex justify-end">
            <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
              Save
            </Button>
          </div>
        </>
      ) : (
        <dl className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <dt className="text-xs text-muted-foreground">Phone</dt>
            <dd>{e.phone || '—'}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Emergency contact</dt>
            <dd>{e.emergency_contact || '—'}</dd>
          </div>
        </dl>
      )}
      {isAdmin && (
        <div className="space-y-2 rounded-lg border p-3">
          <div className="text-sm font-medium">{e.status === 'left' ? 'Left the business' : 'Leaving'}</div>
          {e.status === 'left' ? (
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">Last day {e.exit_date}. Their leaving month is still paid by payroll.</p>
              <Button variant="outline" size="sm" onClick={() => rejoin.mutate()} disabled={rejoin.isPending}>
                Working again
              </Button>
            </div>
          ) : (
            <div className="flex flex-wrap items-end gap-2">
              <Field label="Last working day">
                <Input type="date" value={exitDate} onChange={(ev) => setExitDate(ev.target.value)} className="w-44" />
              </Field>
              <Button variant="outline" onClick={() => left.mutate()} disabled={!exitDate || left.isPending}>
                Mark as left
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function PayPane({ e, rates }: { e: HREmployee; rates: HRRate[] }) {
  const qc = useQueryClient()
  const { formatCurrency } = useCurrency()
  const { isAdmin } = hrRole()
  const [amount, setAmount] = useState('')
  const [from, setFrom] = useState(todayISO().slice(0, 8) + '01')
  const [reason, setReason] = useState('')
  const change = useMutation({
    mutationFn: () => apiClient.hrAddRate(e.id, { amount: Number(amount), effective_from: from, reason }),
    onSuccess: () => {
      toastHelpers.success('Pay changed', `From ${from}`)
      setAmount('')
      setReason('')
      qc.invalidateQueries({ queryKey: ['hr'] })
    },
    onError: (err) => toastHelpers.error('Couldn’t change pay', errMsg(err)),
  })
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Stat label={e.pay_type === 'daily' ? 'Daily wage' : 'Monthly salary'} value={payLabel(e, formatCurrency)} hint={e.rate_effective_from ? `since ${e.rate_effective_from}` : undefined} />
        <Stat label="Usually paid by" value={METHOD_LABEL[e.payout_method ?? 'cash']} hint={e.payout_method !== 'cash' ? [e.bank_name, e.account_number].filter(Boolean).join(' ') : undefined} />
      </div>
      {isAdmin && (
        <div className="space-y-3 rounded-lg border p-3">
          <div className="text-sm font-medium">Change pay</div>
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label={e.pay_type === 'daily' ? 'New daily wage' : 'New monthly salary'}>
              <Input type="number" min={0} value={amount} onChange={(ev) => setAmount(ev.target.value)} />
            </Field>
            <Field label="From">
              <Input type="date" value={from} onChange={(ev) => setFrom(ev.target.value)} />
            </Field>
            <Field label="Why">
              <Input value={reason} onChange={(ev) => setReason(ev.target.value)} placeholder="e.g. Yearly raise" />
            </Field>
          </div>
          <p className="text-xs text-muted-foreground">A change in the middle of a month is split by days. Old pay stays on record.</p>
          <div className="flex justify-end">
            <Button onClick={() => change.mutate()} disabled={amount === '' || !from || change.isPending}>
              Save new pay
            </Button>
          </div>
        </div>
      )}
      <div>
        <div className="mb-2 text-sm font-medium">History</div>
        {rates.length === 0 ? (
          <Empty>No pay set yet{isAdmin ? ' — set it above.' : '.'}</Empty>
        ) : (
          <div className="divide-y rounded-lg border text-sm">
            {rates.map((r) => (
              <div key={r.id} className="flex items-center justify-between px-3 py-2">
                <div>
                  <div className="font-medium tabular-nums">{formatCurrency(r.amount)}</div>
                  <div className="text-xs text-muted-foreground">
                    from {r.effective_from}
                    {r.reason ? ` · ${r.reason}` : ''}
                    {r.set_by ? ` · ${r.set_by}` : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function StatementPane({ e }: { e: HREmployee }) {
  const qc = useQueryClient()
  const { formatCurrency } = useCurrency()
  const [paying, setPaying] = useState(false)
  const [advancing, setAdvancing] = useState(false)
  const q = useQuery({ queryKey: ['hr', 'statement', e.id], queryFn: () => apiClient.hrStatement(e.id) })
  const st = q.data?.data as HRStatement | undefined
  const emp = st?.employee ?? e
  const due = Math.max(0, emp.salary_due ?? 0)
  const exportCSV = () =>
    st &&
    downloadCSV(`statement-${emp.full_name.replace(/\s+/g, '-').toLowerCase()}.csv`, [
      ['Date', 'What', 'Note', 'Amount', 'Balance'],
      ...st.lines.map((l) => [l.date, ENTRY_LABEL[l.entry_type], l.note ?? '', l.amount, l.balance]),
    ])
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Owed to them" value={formatCurrency(due)} />
        <Stat label="Advance they owe" value={formatCurrency(emp.advance_outstanding ?? 0)} />
        <Stat label="Net position" value={formatCurrency(emp.balance ?? 0)} hint="owed − advances" />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={() => setPaying(true)} disabled={due <= 0}>
          Pay
        </Button>
        <Button size="sm" variant="outline" onClick={() => setAdvancing(true)}>
          Give advance
        </Button>
        <Button size="sm" variant="outline" onClick={exportCSV} disabled={!st?.lines.length}>
          Export
        </Button>
      </div>
      {st && st.advances.some((a) => a.outstanding > 0) && (
        <div className="space-y-1">
          {st.advances
            .filter((a) => a.outstanding > 0)
            .map((a) => (
              <Banner key={a.id}>
                Advance of {formatCurrency(a.amount)} ({a.given_on}) — {formatCurrency(a.outstanding)} left · {RECOVERY_LABEL[a.recovery_plan]}
                {a.installments ? ` over ${a.installments} months` : ''}
              </Banner>
            ))}
        </div>
      )}
      {!st ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : st.lines.length === 0 ? (
        <Empty>Nothing yet. Their first approved payroll, advance or payment shows here.</Empty>
      ) : (
        <div className="overflow-hidden rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">Date</th>
                <th className="px-3 py-2 font-medium">What</th>
                <th className="px-3 py-2 text-right font-medium">Amount</th>
                <th className="px-3 py-2 text-right font-medium">Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {[...st.lines].reverse().map((l) => (
                <tr key={l.id}>
                  <td className="whitespace-nowrap px-3 py-2 tabular-nums text-muted-foreground">{dayLabel(l.date, true)}</td>
                  <td className="px-3 py-2">
                    <div>{ENTRY_LABEL[l.entry_type]}{l.run_month && l.entry_type !== 'payout' ? ` · ${monthLabel(l.run_month)}` : ''}</div>
                    {l.note && l.note !== ENTRY_LABEL[l.entry_type] && <div className="text-xs text-muted-foreground">{l.note}</div>}
                  </td>
                  <td className={cn('whitespace-nowrap px-3 py-2 text-right tabular-nums', l.amount < 0 && 'text-muted-foreground')}>
                    {l.amount > 0 ? '+' : '−'}
                    {formatCurrency(Math.abs(l.amount))}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">{formatCurrency(l.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <PayDialog open={paying} onClose={() => { setPaying(false); invalidateHRMoney(qc) }} person={emp} maxAmount={due} />
      <AdvanceDialog open={advancing} onClose={() => setAdvancing(false)} people={[emp]} personId={emp.id} />
    </div>
  )
}

function LeavePane({ e }: { e: HREmployee }) {
  const bq = useQuery({ queryKey: ['hr', 'leave-balances'], queryFn: () => apiClient.hrLeaveBalances() })
  const eq = useQuery({ queryKey: ['hr', 'leave-entries', e.id], queryFn: () => apiClient.hrListLeaveEntries(e.id) })
  const bal = bq.data?.data as HRLeaveBalances | undefined
  const mine = bal?.people.find((p) => p.employee_id === e.id)
  const entries = (eq.data?.data as HRLeaveEntry[] | undefined) ?? []
  return (
    <div className="space-y-4">
      {mine && bal ? (
        <>
          <p className="text-xs text-muted-foreground">
            Leave year {mine.year_start} – {mine.year_end}
          </p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {bal.types.map((t) => {
              const b = mine.balances.find((x) => x.leave_type_id === t.id)
              if (!b) return null
              return (
                <Stat
                  key={t.id}
                  label={`${t.name} (${t.code})`}
                  value={`${b.available} left`}
                  tone={b.available < 0 ? 'danger' : undefined}
                  hint={`${b.entitled}${b.carried ? ` + ${b.carried} carried` : ''}${b.adjusted ? ` ${b.adjusted > 0 ? '+' : '−'} ${Math.abs(b.adjusted)} adj.` : ''} · ${b.used} taken`}
                />
              )
            })}
          </div>
        </>
      ) : (
        <div className="text-sm text-muted-foreground">{e.status === 'left' ? 'No current leave year.' : 'Loading…'}</div>
      )}
      <div>
        <div className="mb-2 text-sm font-medium">Leave taken</div>
        {entries.length === 0 ? (
          <Empty>No leave recorded.</Empty>
        ) : (
          <div className="divide-y rounded-lg border text-sm">
            {entries.map((x) => (
              <div key={x.id} className={cn('flex items-center justify-between px-3 py-2', x.cancelled && 'opacity-50')}>
                <div>
                  <div className="font-medium">
                    {x.leave_type} · {x.from === x.to ? dayLabel(x.from, true) : `${dayLabel(x.from)} – ${dayLabel(x.to, true)}`}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {x.cancelled ? 'Cancelled' : `${x.days} ${x.days === 1 ? 'day' : 'days'}`}
                    {x.note ? ` · ${x.note}` : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
